import {useCallback} from 'react';
import {useIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import {PERIOD_BUDGET_TYPE} from '../../../../constants';
import {sortByStartDate} from '../project-retainer-periods/RetainerPeriodUtil';

/**
 * Custom hook for exporting retainer period data to CSV
 * @param {Object} project - The project object containing retainer periods
 * @param {string} currency - The currency symbol
 * @param {Object} enabledColumns - Object mapping column names to enabled state
 * @returns {Function} exportCSV - Function to trigger CSV export
 */
export const useRetainerPeriodCSVExport = (project, currency, enabledColumns) => {
	const intl = useIntl();
	const {formatNumber, formatDate} = intl;

	const exportCSV = useCallback(() => {
		if (!project || !project.retainerPeriods || !project.retainerPeriods.edges) {
			return;
		}

		// Use shallow copy instead of cloneDeep for better performance
		const sortedPeriods = [...project.retainerPeriods.edges].sort(sortByStartDate).map(period => period.node);
		const inHours =
			project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS ||
			project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS;
		const format = inHours ? 'rounded_two_decimal' : 'always_two_decimal';
		const colNameSuffix = inHours ? '(min)' : `(${Util.GetCurrencySymbol(currency)})`;

		// Memoize date formatting options
		const dateFormatOptions = {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		};

		// Build column names array
		const columnNames = ['name', 'locked'];
		if (enabledColumns['dates']) columnNames.push('dates');
		if (enabledColumns['progress']) columnNames.push('progress');
		if (enabledColumns['target']) columnNames.push('target ' + colNameSuffix);
		if (enabledColumns['net-rollover']) columnNames.push('net-rollover ' + colNameSuffix);
		if (enabledColumns['adjusted-target']) columnNames.push('adjusted-target ' + colNameSuffix);
		if (enabledColumns['actual-billable-value-of-service'])
			columnNames.push('actual-billable-value-of-service ' + colNameSuffix);
		if (enabledColumns['actual-budget-hours-remaining']) columnNames.push('actual-budget-hours-remaining ' + colNameSuffix);
		if (enabledColumns['actual-percentage-target-used']) columnNames.push('actual-percentage-target-used');
		if (enabledColumns['projected-total']) columnNames.push('projected-total ' + colNameSuffix);
		if (enabledColumns['projected-total-budget-remaining'])
			columnNames.push('projected-total-budget-remaining ' + colNameSuffix);

		// Build CSV rows using array for better performance
		const rows = [columnNames.join(',')];

		sortedPeriods.forEach(period => {
			const financialNumbers = period.financialNumbers;
			const periodTarget = inHours
				? financialNumbers.retainerPeriodTargetMinutes
				: financialNumbers.retainerPeriodTargetPrice;
			const periodDifference = inHours
				? financialNumbers.retainerPeriodRolloverMinutes
				: financialNumbers.retainerPeriodRolloverPrice;
			const periodTargetTotal = periodTarget + periodDifference;
			const actualMinutes = financialNumbers.billableActualMinutes;
			const actualPrice = financialNumbers.billableActualTimeAndExpenses;
			const billableActualValueOfService = inHours ? actualMinutes : actualPrice;
			const actualBudgetRemaining = inHours ? periodTargetTotal - actualMinutes : periodTargetTotal - actualPrice;
			const remainingPlanForPeriod = inHours
				? financialNumbers.remainingMinutes
				: financialNumbers.billableForecastTimeAndExpensesToComplete;
			const periodProgress =
				periodTargetTotal <= 0 ? 1 : inHours ? actualMinutes / periodTargetTotal : actualPrice / periodTargetTotal;
			const periodForecast = inHours ? actualMinutes + remainingPlanForPeriod : actualPrice + remainingPlanForPeriod;
			const projectedTotalBudgetRemaining = periodTargetTotal - periodForecast;

			// Build date string
			const startDateStr = period.startYear
				? formatDate(
						Util.CreateNonUtcMomentDate(period.startYear, period.startMonth, period.startDay),
						dateFormatOptions
				  )
				: '';
			const endDateStr = period.endYear
				? formatDate(Util.CreateNonUtcMomentDate(period.endYear, period.endMonth, period.endDay), dateFormatOptions)
				: '';
			const dateString = startDateStr && endDateStr ? `${startDateStr} - ${endDateStr}` : startDateStr || endDateStr;

			// Build row data array
			const escapedName = period.name.replace(/"/g, '""');
			const rowData = [`"${escapedName}"`, period.periodLocked ? 'yes' : 'no'];

			if (enabledColumns['dates']) rowData.push(`"${dateString}"`);
			if (enabledColumns['target']) rowData.push(`"${formatNumber(periodTarget, {format: format})}"`);
			if (enabledColumns['net-rollover']) rowData.push(`"${formatNumber(periodDifference, {format: format})}"`);
			if (enabledColumns['adjusted-target']) rowData.push(`"${formatNumber(periodTargetTotal, {format: format})}"`);
			if (enabledColumns['actual-billable-value-of-service'])
				rowData.push(`"${formatNumber(billableActualValueOfService, {format: format})}"`);
			if (enabledColumns['actual-budget-hours-remaining'])
				rowData.push(`"${formatNumber(actualBudgetRemaining, {format: format})}"`);
			if (enabledColumns['actual-percentage-target-used'])
				rowData.push(
					`"${formatNumber(periodProgress, {
						style: 'decimal',
						minimumFractionDigits: 4,
						maximumFractionDigits: 4,
					})}"`
				);
			if (enabledColumns['projected-total']) rowData.push(`"${formatNumber(periodForecast, {format: format})}"`);
			if (enabledColumns['projected-total-budget-remaining'])
				rowData.push(`"${formatNumber(projectedTotalBudgetRemaining, {format: format})}"`);

			rows.push(rowData.join(','));
		});

		const exportData = rows.join('\r\n');
		const fileName = project.name.replace(/\s/g, '_').toLowerCase() + '_retainer_period_data.csv';
		Util.exportToCSV(exportData, fileName);
	}, [project, currency, enabledColumns, formatNumber, formatDate]);

	return exportCSV;
};
